import React, { useState } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import LayersClearOutlinedIcon from "@mui/icons-material/LayersClearOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useData } from "../hooks/useData";
import { ActiveSelection } from "fabric";
import LayersDraggable from "./LayersDraggable"; // Import the new component

const EditMenu = ({ canvas, handleUndo, handleRedo, canUndo, canRedo }) => {
  const { setSnackbar, saveHistory } = useData();
  const [anchorEl, setAnchorEl] = useState(null);
  const [clipboardObj, setClipboardObj] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [layersDialogOpen, setLayersDialogOpen] = useState(false); // State for LayersDraggable dialog
  const open = Boolean(anchorEl);

  const isDeleteDisabled = !canvas || !canvas.getActiveObject();
  const isCopyDisabled = !canvas || !canvas.getActiveObject();
  const pasteDisabled = !clipboardObj;
  const isUndoDisabled = !canUndo;
  const isRedoDisabled = !canRedo;
  const isLayersDisabled = !canvas || canvas.getObjects().length === 0; // Disable if no objects

  const handleClick = (event) => {
    console.log("[EditMenu]: handleClick");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    console.log("[EditMenu]: handleClose");
    setAnchorEl(null);
  };

  const handleDeleteLayer = () => {
    console.log("[EditMenu]: handleDeleteLayer");
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        saveHistory(canvas);
        if (activeObject instanceof ActiveSelection) {
          activeObject.getObjects().forEach((obj) => {
            canvas.remove(obj);
          });
        } else {
          canvas.remove(activeObject);
        }
        canvas.discardActiveObject();
        canvas.requestRenderAll();
        setSnackbarMessage("Layer deleted successfully!");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error deleting layer:", error);
        setSnackbar({
          open: true,
          message: "Failed to delete layer.",
          severity: "error",
        });
      }
    }
    setAnchorEl(null);
  };

  const handleCopy = async () => {
    console.log("[EditMenu]: handleCopy");
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      try {
        const clonedObj = await activeObject.clone();
        setClipboardObj(clonedObj);
        setSnackbarMessage("Object copied successfully!");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Error copying object:", error);
        setSnackbar({
          open: true,
          message: "Failed to copy object.",
          severity: "error",
        });
      }
    }
    setAnchorEl(null);
  };

  const handlePaste = async () => {
    console.log("[EditMenu]: handlePaste");
    if (!clipboardObj) return;

    try {
      saveHistory(canvas);
      const clonedObj = await clipboardObj.clone();
      canvas.discardActiveObject();

      clonedObj.set({
        left: clonedObj.left + 30,
        top: clonedObj.top + 30,
        evented: true,
      });

      if (clonedObj instanceof ActiveSelection) {
        clonedObj.canvas = canvas;
        clonedObj.forEachObject((obj) => {
          canvas.add(obj);
        });
        clonedObj.setCoords();
      } else {
        await canvas.add(clonedObj);
      }

      clonedObj.top += 10;
      clonedObj.left += 10;
      clonedObj.set({
        top: clonedObj.top,
        left: clonedObj.left,
      });

      canvas.setActiveObject(clonedObj);
      canvas.requestRenderAll();

      setSnackbarMessage("Object pasted successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error pasting object:", error);
      setSnackbar({
        open: true,
        message: "Failed to paste object.",
        severity: "error",
      });
    }
    setAnchorEl(null);
  };

  const handleViewLayers = () => {
    console.log("[EditMenu]: handleViewLayers");
    setLayersDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCloseFile = () => {
    console.log("[EditMenu]: handleCloseFile");
    setAnchorEl(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  return (
    <>
      <Typography
        variant="body1"
        onClick={handleClick}
        sx={{
          fontFamily: "Roboto",
          fontWeight: 500,
          color: "#000000",
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
            color: "#000000",
          },
        }}
      >
        Edit
      </Typography>
      <Menu
        id="edit-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        MenuListProps={{
          "aria-labelledby": "edit-button",
        }}
        sx={{
          "& .MuiPaper-root": {
            minWidth: 200,
            maxWidth: 240,
            boxShadow: 3,
          },
        }}
      >
        <MenuItem disabled={isUndoDisabled} onClick={handleUndo}>
          <UndoOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Undo
        </MenuItem>
        <MenuItem disabled={isRedoDisabled} onClick={handleRedo}>
          <RedoOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Redo
        </MenuItem>
        <Divider />
        <MenuItem disabled={isDeleteDisabled} onClick={handleDeleteLayer}>
          <LayersClearOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete layer
        </MenuItem>
        <MenuItem disabled={isCopyDisabled} onClick={handleCopy}>
          <ContentCopyOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Copy Item
        </MenuItem>
        <MenuItem disabled={pasteDisabled} onClick={handlePaste}>
          <LibraryAddOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Paste Item
        </MenuItem>
        <Divider />
        <MenuItem disabled={isLayersDisabled} onClick={handleViewLayers}>
          <LayersOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          View Layers
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleCloseFile}>
          <CloseOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
          Close
        </MenuItem>
      </Menu>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <LayersDraggable
        canvas={canvas}
        open={layersDialogOpen}
        onClose={() => setLayersDialogOpen(false)}
      />
    </>
  );
};

export default EditMenu;
